import { useContext, useEffect, useMemo, useRef } from 'react';
import { FILTER_TYPES } from 'common/constants/filters';
import useLinbTeamsEntities from './useLinbTeamsEntities';
import useLinbPdtEntities from './useLinbPdtEntities';
import _ from 'lodash';
import useFiltersPayloadIssuesKeys from './useFiltersPayloadIssuesKeys';
import useIsTreeHierarchyEnabled from './useIsTreeHierarchyEnabled';
import useContextActions from '../useContextActions';

const useFiltersPayload = ({ Context }) => {
  const { state } = useContext(Context);
  const {
    projects = [],
    board = [],
    label = [],
    fixVersion = [],
    issue_parent_1 = [],
    issue_parent_2 = [],
    team: teams = [],
    [FILTER_TYPES.ORGANIZATION_PDT]: selectedPdtEntities = [],
    [FILTER_TYPES.DYNAMIC_ISSUES]: dynamicIssues = [],
    ...customFields
  } = state.filters;
  const contextActions = useContextActions({ Context });
  const prevFiltersPayload = useRef({});
  const issuesKeys = useFiltersPayloadIssuesKeys();
  const isTreeHierarchyEnabled = useIsTreeHierarchyEnabled(Context);

  const teamsIds = useMemo(
    () => teams.map(currTeam => currTeam?.id).filter(Boolean),
    [teams]
  );
  const teamsEntities = useLinbTeamsEntities({
    teamsIds,
    isTreeHierarchyEnabled
  });
  const pdtEntities = useLinbPdtEntities({
    pdtEntities: state.pdtEntities,
    selectedPdtEntities,
    isTreeHierarchyEnabled
  });

  const projectIds = useMemo(
    () => projects.map(currProject => currProject?.id).filter(Boolean),
    [projects]
  );

  const boardIds = useMemo(
    () => (board || []).map(currBoard => currBoard?.id).filter(Boolean),
    [board]
  );

  const labels = useMemo(
    () => label.map(currLabel => currLabel?.title).filter(Boolean),
    [label]
  );

  const fixVersionsNames = useMemo(
    () =>
      fixVersion.map(currFixVersion => currFixVersion?.title).filter(Boolean),
    [fixVersion]
  );

  const fields = useMemo(
    () =>
      Object.keys(customFields).reduce((acc, fieldKey) => {
        const fieldValues = (customFields[fieldKey] || [])
          .map(value => value.title)
          .filter(Boolean);

        return {
          ...acc,
          ...(fieldValues?.length > 0 && { [fieldKey]: fieldValues })
        };
      }, {}),
    [customFields]
  );

  const issues = useMemo(() => {
    const p1Values = issue_parent_1.map(item => item?.id).filter(Boolean);
    const p2Values = issue_parent_2.map(item => item?.id).filter(Boolean);

    const issues = {
      [issuesKeys.parentOne]: p1Values,
      [issuesKeys.parentTwo]: p2Values
    };
    return _.omitBy(issues, _.isEmpty);
  }, [issue_parent_1, issue_parent_2]);

  const dynamicIssuesIds = useMemo(() => {
    const issueIds = dynamicIssues.map(issue => issue?.id).filter(Boolean);
    return issueIds;
  }, [dynamicIssues]);

  const payload = {
    projects_ids: projectIds,
    boards_ids: boardIds,
    labels,
    fix_versions: fixVersionsNames,
    ...(isTreeHierarchyEnabled ? { issue_ids: dynamicIssuesIds } : { issues }),

    fields,
    linearb_teams: teamsEntities,
    linearb_projects: pdtEntities
  };
  const filteredPayload = _.omitBy(payload, _.isEmpty);

  useEffect(() => {
    if (!_.isEqual(prevFiltersPayload.current, filteredPayload)) {
      prevFiltersPayload.current = filteredPayload;
      contextActions.onUpdateFiltersPayload(filteredPayload);
    }
  }, [filteredPayload]);
};

export default useFiltersPayload;
